import { forwardRef } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn } from "../lib/cn";

/**
 * Popover — free-form anchored panel on @radix-ui/react-popover.
 *
 *   <Popover>
 *     <PopoverTrigger asChild><Button size="sm">Filter</Button></PopoverTrigger>
 *     <PopoverContent align="start">…anything…</PopoverContent>
 *   </Popover>
 *
 * `PopoverTrigger` and `PopoverAnchor` are unstyled passthroughs — wrap
 * the anchor control with `asChild`, or use `PopoverAnchor` to pin the
 * panel to an element that is not its trigger.
 */

export interface PopoverProps
  extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Root> {}

/** Controlled/uncontrolled root. Renders no DOM element. */
export function Popover(props: PopoverProps) {
  return <PopoverPrimitive.Root {...props} />;
}
Popover.displayName = "Popover";

export interface PopoverTriggerProps
  extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger> {}

/** Unstyled anchor — use `asChild` around a Button or icon button. */
export const PopoverTrigger = forwardRef<
  React.ComponentRef<typeof PopoverPrimitive.Trigger>,
  PopoverTriggerProps
>(({ className, ...props }, ref) => {
  return (
    <PopoverPrimitive.Trigger ref={ref} className={className} {...props} />
  );
});
PopoverTrigger.displayName = "PopoverTrigger";

export interface PopoverAnchorProps
  extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Anchor> {}

/** Positioning-only anchor — pairs with an external trigger. */
export const PopoverAnchor = forwardRef<
  React.ComponentRef<typeof PopoverPrimitive.Anchor>,
  PopoverAnchorProps
>(({ className, ...props }, ref) => {
  return (
    <PopoverPrimitive.Anchor ref={ref} className={className} {...props} />
  );
});
PopoverAnchor.displayName = "PopoverAnchor";

export interface PopoverContentProps
  extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> {}

/** Portalled raised panel — same grammar as the Menu panel, roomier padding. */
export const PopoverContent = forwardRef<
  React.ComponentRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(({ className, sideOffset = 6, ...props }, ref) => {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        collisionPadding={8}
        className={cn(
          "z-(--z-popover) rounded-lg border border-border-default bg-surface-raised p-4 shadow-popover outline-none",
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
});
PopoverContent.displayName = "PopoverContent";
